import { useEffect, useState } from 'react'

// localStorage-backed useState. Filter / search / sort state on list pages
// (Deliveries, Runs, Pallets) used to live in plain useState — meaning a
// detail-page round trip wiped the filter, which was annoying when an
// operator was working through a long filtered list. Persisting to
// localStorage means the filter sticks until the operator clicks Clear.
//
// Safe-by-default: SSR-safe (window check), JSON-encoded so any state
// shape works, falls back to the initial value if the stored blob is
// malformed (so a stale schema after a deploy doesn't crash the page).
// Shared by useTenantPersistedState — one read/write implementation so the
// plain and tenant-scoped hooks can never drift on JSON/fallback semantics.
export function readPersisted<T>(key: string, initial: T): T {
  if (typeof window === 'undefined') return initial
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null) return initial
    return JSON.parse(raw) as T
  } catch {
    return initial
  }
}

export function writePersisted<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Quota or private-mode — silently ignore. The filter just won't
    // persist on this device, which is the same as the old behaviour.
  }
}

export function usePersistedState<T>(key: string, initial: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readPersisted(key, initial))

  useEffect(() => {
    writePersisted(key, value)
  }, [key, value])

  return [value, setValue]
}
